import { FC, useEffect, useRef, useState } from "react";

import {
  AIAssessmentComparisonRecord,
  HumanAssessedSubmission,
  buildAIAssessmentComparisonCsv,
  runAIAssessmentEvaluationQueue,
} from "./evaluation";

import { DEFAULT_MODEL_FOR_ASSESSMENT_QUERY } from "components/ai/components/AISettings";
import { serverEvaluateSubmission } from "components/ai/queries/assessment-query";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useAppSelector } from "store";
import { NestedPageOutcomeType, pageSelectors, selectCurrentSectionId } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { filterMap } from "utils/collections";
import { downloadCSVFile } from "utils/file";

type EvaluationStage = "confirm" | "running" | "canceling" | "complete" | "canceled";
type EvaluationRunMode = "initial" | "retry";
type AIAssessmentEvaluationRequestResult = Pick<
  AIAssessmentComparisonRecord,
  "aiResponse" | "evaluationData" | "errorMessage" | "generatedAt" | "durationMs"
>;

// Initial planning estimate, not an API timeout: two requests run concurrently at 30 seconds each.
// The CSV records duration_ms per submission so real test runs can inform a later adjustment.
const EVALUATION_CONCURRENCY = 2;
const ESTIMATED_SECONDS_PER_SUBMISSION = 30;
const LARGE_EVALUATION_SUBMISSION_COUNT = 25;
const MAX_FOREGROUND_EVALUATION_SUBMISSION_COUNT = 50;

type AIAssessmentEvaluationDialogProps = {
  humanAssessedSubmissions: HumanAssessedSubmission[];
  pageId: string;
  pageOutcomes: NestedPageOutcomeType[];
  sectionTitle: string;
  feedbackStyleApplied: boolean;
  onClose: () => void;
};

export const AIAssessmentEvaluationDialog: FC<AIAssessmentEvaluationDialogProps> = ({
  humanAssessedSubmissions,
  pageId,
  pageOutcomes,
  sectionTitle,
  feedbackStyleApplied,
  onClose,
}) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["title"]);
  const [stage, setStage] = useState<EvaluationStage>("confirm");
  const [comparisonRecordsBySubmissionId, setComparisonRecordsBySubmissionId] = useState<
    Record<string, AIAssessmentComparisonRecord>
  >({});
  const [processedSubmissionCount, setProcessedSubmissionCount] = useState(0);
  const [evaluationSubmissionCount, setEvaluationSubmissionCount] = useState(
    humanAssessedSubmissions.length,
  );
  const evaluationAbortControllerRef = useRef<AbortController | null>(null);
  const isEvaluationRunActiveRef = useRef(false);
  const evaluationRunTokenRef = useRef(0);
  const humanAssessedSubmissionsSnapshotRef =
    useRef<HumanAssessedSubmission[]>(humanAssessedSubmissions);
  const estimatedMinutes = Math.max(
    1,
    Math.ceil(
      (humanAssessedSubmissions.length / EVALUATION_CONCURRENCY) *
        (ESTIMATED_SECONDS_PER_SUBMISSION / 60),
    ),
  );
  const exceedsForegroundSubmissionLimit =
    humanAssessedSubmissions.length > MAX_FOREGROUND_EVALUATION_SUBMISSION_COUNT;

  useEffect(
    () => () => {
      isEvaluationRunActiveRef.current = false;
      evaluationRunTokenRef.current += 1;
      evaluationAbortControllerRef.current?.abort();
    },
    [],
  );

  const runEvaluation = async (
    submissionsToEvaluate: HumanAssessedSubmission[],
    runMode: EvaluationRunMode,
  ) => {
    if (isEvaluationRunActiveRef.current) return;
    isEvaluationRunActiveRef.current = true;
    evaluationRunTokenRef.current += 1;
    const evaluationRunToken = evaluationRunTokenRef.current;
    const abortController = new AbortController();
    evaluationAbortControllerRef.current = abortController;
    if (runMode === "initial") {
      humanAssessedSubmissionsSnapshotRef.current = [...humanAssessedSubmissions];
      setComparisonRecordsBySubmissionId({});
    }
    setProcessedSubmissionCount(0);
    setEvaluationSubmissionCount(submissionsToEvaluate.length);
    setStage("running");

    await runAIAssessmentEvaluationQueue<AIAssessmentEvaluationRequestResult>({
      concurrency: EVALUATION_CONCURRENCY,
      humanAssessedSubmissions: submissionsToEvaluate,
      signal: abortController.signal,
      evaluateSubmission: async (humanAssessedSubmission) => {
        const startedAt = performance.now();
        const evaluationResponse = await serverEvaluateSubmission(
          humanAssessedSubmission.submission,
          DEFAULT_MODEL_FOR_ASSESSMENT_QUERY,
        );
        const { evaluationData, ...aiResponse } = evaluationResponse;
        return {
          aiResponse: aiResponse.errorMessage ? undefined : aiResponse,
          evaluationData,
          errorMessage: aiResponse.errorMessage,
          generatedAt: new Date().toISOString(),
          durationMs: Math.round(performance.now() - startedAt),
        };
      },
      onEvaluationSettled: ({
        humanAssessedSubmission,
        result: evaluationResult,
        errorMessage,
      }) => {
        if (evaluationRunToken !== evaluationRunTokenRef.current) return;
        setComparisonRecordsBySubmissionId((current) => ({
          ...current,
          [humanAssessedSubmission.submission.id]: {
            humanAssessedSubmission,
            aiResponse: evaluationResult?.aiResponse,
            evaluationData: evaluationResult?.evaluationData,
            errorMessage: evaluationResult?.errorMessage ?? errorMessage,
            generatedAt: evaluationResult?.generatedAt ?? new Date().toISOString(),
            durationMs: evaluationResult?.durationMs ?? 0,
          },
        }));
        setProcessedSubmissionCount((current) => current + 1);
      },
    });

    if (evaluationRunToken !== evaluationRunTokenRef.current) return;
    isEvaluationRunActiveRef.current = false;
    setStage(abortController.signal.aborted && runMode === "initial" ? "canceled" : "complete");
  };

  const cancelEvaluationRun = () => {
    evaluationAbortControllerRef.current?.abort();
    setStage("canceling");
  };

  const comparisonRecords = humanAssessedSubmissionsSnapshotRef.current
    .map(
      (humanAssessedSubmission) =>
        comparisonRecordsBySubmissionId[humanAssessedSubmission.submission.id],
    )
    .filter((record): record is AIAssessmentComparisonRecord => Boolean(record));
  const failedHumanAssessedSubmissions = filterMap(
    comparisonRecords,
    (comparisonRecord) => Boolean(comparisonRecord.errorMessage),
    (comparisonRecord) => comparisonRecord.humanAssessedSubmission,
  );
  const refusedCount = comparisonRecords.filter(
    (comparisonRecord) => !comparisonRecord.errorMessage && comparisonRecord.aiResponse?.refused,
  ).length;
  const successCount =
    comparisonRecords.length - failedHumanAssessedSubmissions.length - refusedCount;

  const downloadComparisonCsv = () => {
    if (!sectionId || !page) return;
    const comparisonCsv = buildAIAssessmentComparisonCsv(
      comparisonRecords,
      {
        sectionId,
        sectionTitle,
        pageId,
        pageTitle: page.title,
        model: DEFAULT_MODEL_FOR_ASSESSMENT_QUERY,
      },
      pageOutcomes,
    );
    const date = new Date().toISOString().slice(0, 10);
    const filename = `ForumAIAssessmentComparison-${pageId}-${date}.csv`;
    trackEvent("AI assessment evaluation - Download CSV", {
      ...BUTTON_EVENT_CATEGORY,
      context: sectionId,
      pageId,
      fileName: filename,
    });
    downloadCSVFile(comparisonCsv, filename);
  };

  const isRunning = stage === "running";
  const isDraining = stage === "canceling";
  const canClose = !isRunning && !isDraining;
  return (
    <Dialog
      dialogPanelClassName="w-[560px] max-w-[calc(100vw-32px)]"
      dismissible={canClose}
      title={t("assessment.ai_evaluation.title")}
      open
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        {stage === "confirm" && (
          <>
            <p>{t("assessment.ai_evaluation.description")}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <dt className="font-medium">{t("assessment.ai_evaluation.submissions")}</dt>
              <dd>{humanAssessedSubmissions.length}</dd>
              <dt className="font-medium">{t("assessment.ai_evaluation.model")}</dt>
              <dd>{DEFAULT_MODEL_FOR_ASSESSMENT_QUERY}</dd>
              <dt className="font-medium">{t("assessment.ai_evaluation.feedback_style")}</dt>
              <dd>{t(feedbackStyleApplied ? "common.yes" : "common.no")}</dd>
            </dl>
            <p>{t("assessment.ai_evaluation.no_changes")}</p>
            <p className="text-orange-shade-20">
              {t("assessment.ai_evaluation.student_data_warning")}
            </p>
            <p>
              {t("assessment.ai_evaluation.estimated_time", {
                count: estimatedMinutes,
              })}
            </p>
            {humanAssessedSubmissions.length > LARGE_EVALUATION_SUBMISSION_COUNT &&
              !exceedsForegroundSubmissionLimit && (
                <p className="text-orange-shade-20">{t("assessment.ai_evaluation.large_run")}</p>
              )}
            {exceedsForegroundSubmissionLimit && (
              <p className="text-red-tint-20">{t("assessment.ai_evaluation.too_large")}</p>
            )}
          </>
        )}

        {stage === "running" && (
          <>
            <p aria-live="polite">
              {t("assessment.ai_evaluation.progress", {
                processed: processedSubmissionCount,
                total: evaluationSubmissionCount,
              })}
            </p>
            <p>{t("assessment.ai_evaluation.cancel_explanation")}</p>
          </>
        )}

        {stage === "complete" && (
          <p aria-live="polite">
            {t("assessment.ai_evaluation.complete", {
              success: successCount,
              refused: refusedCount,
              failed: failedHumanAssessedSubmissions.length,
            })}
          </p>
        )}

        {stage === "canceling" && (
          <p aria-live="polite">{t("assessment.ai_evaluation.canceling")}</p>
        )}
        {stage === "canceled" && <p>{t("assessment.ai_evaluation.canceled")}</p>}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {(isRunning || isDraining) && (
            <SquareButton
              aria-disabled={isDraining}
              kind="tertiary"
              size="m"
              autoFocus
              onClick={isRunning ? cancelEvaluationRun : undefined}
            >
              {t("assessment.ai_evaluation.cancel_run")}
            </SquareButton>
          )}
          {canClose && (
            <SquareButton
              autoFocus={stage === "complete" || exceedsForegroundSubmissionLimit}
              kind="tertiary"
              size="m"
              onClick={onClose}
            >
              {t("common.close")}
            </SquareButton>
          )}
          {(stage === "confirm" || stage === "canceled") && !exceedsForegroundSubmissionLimit && (
            <SquareButton
              kind="accent"
              size="m"
              autoFocus
              onClick={() => runEvaluation(humanAssessedSubmissions, "initial")}
            >
              {t("assessment.ai_evaluation.start")}
            </SquareButton>
          )}
          {stage === "complete" && failedHumanAssessedSubmissions.length > 0 && (
            <SquareButton
              kind="tertiary"
              size="m"
              onClick={() => runEvaluation(failedHumanAssessedSubmissions, "retry")}
            >
              {t("assessment.ai_evaluation.retry_failed", {
                count: failedHumanAssessedSubmissions.length,
              })}
            </SquareButton>
          )}
          {stage === "complete" && successCount + refusedCount > 0 && (
            <SquareButton kind="accent" size="m" onClick={downloadComparisonCsv}>
              {t("assessment.ai_evaluation.download")}
            </SquareButton>
          )}
        </div>
      </div>
    </Dialog>
  );
};
